import { CategoryLeaderboard } from "../../components/ui/categryleaderboard";
import { Button } from "../../components/ui/button";
import { ArrowLeftCircle } from "lucide-react";
import { Building, Loader2 } from "lucide-react";
import { SearchableSelect } from "../../components/ui/searchable-select";
import React, { useRef, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getLeaderboard, type CategoryData, type PerformanceData } from "../../services/leaderboardapi";
import { toast } from "react-hot-toast";

type UserRole = "ADMIN" | "SHM" | "HM" | "AGENCY" | "HR" | "FBDE";


function parseJwt(token: string) {
  try {
    const base64Url = token.split(".")[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split("")
        .map(function (c) {
          return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
        })
        .join("")
    );
    return JSON.parse(jsonPayload);
  } catch (e) {
    return null;
  }
}

export const Leaderboard = () => {
  const formTopRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const [userRole, setUserRole] = useState<UserRole | undefined>(undefined);
  const [selectedField, setSelectedField] = useState<string>("all");
  const [selectedPeriod, setSelectedPeriod] = useState<string>("weekly");
  const [leaderboardData, setLeaderboardData] = useState<CategoryData[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const fieldOptions = [
    { label: "All Fields", value: "all" },
    { label: "Joinings", value: "joinings" },
    { label: "Selections", value: "selections" },
    { label: "Walkins", value: "walkins" },
  ];

  // NEW: Period options
  const periodOptions = [
    { label: "Daily", value: "daily" },
    { label: "Weekly", value: "weekly" },
    { label: "Monthly", value: "monthly" },
    { label: "Quarterly", value: "quarterly" },
  ];

  // Fetch user role from token
  useEffect(() => {
    const token = localStorage.getItem("accessToken");
    if (token) {
      const decodedToken = parseJwt(token);
      if (decodedToken && decodedToken.role) {
        console.log("User role from token:", decodedToken.role);
        setUserRole(decodedToken.role.toUpperCase());
      } else {
        console.log("Role not found in token or token is invalid.");
      }
    } else {
      console.log("Access token not found in localStorage.");
    }
  }, []);

  // Fetch leaderboard data
  const fetchLeaderboardData = async () => {
    setIsLoading(true);
    try {
      console.log("Fetching leaderboard with:", { selectedPeriod, selectedField });
      const response = await getLeaderboard(selectedPeriod, selectedField, 10);
      console.log("Leaderboard response:", response);
      // Map the API response to include hirings field (same as joinings for backward compatibility)
      const mappedCategories = response.data.categories.map(category => ({
        ...category,
        performers: category.performers.map(performer => ({
          ...performer,
          hirings: performer.joinings // Map joinings to hirings for UI compatibility
        }))
      }));
      setLeaderboardData(mappedCategories);
    } catch (error: any) {
      console.error("Error fetching leaderboard data:", error);
      console.error("Error response:", error?.response);
      console.error("Error message:", error?.message);
      const errorMessage = error?.response?.data?.message || error?.message || "Failed to fetch leaderboard data";
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch leaderboard data when period or field changes
  useEffect(() => {
    if (userRole) {
      fetchLeaderboardData();
    }
  }, [selectedPeriod, selectedField, userRole]);

  // Use real data from API
  const leaderboardToShow = leaderboardData;

  return (
    <div className="min-h-screen bg-background py-8 px-0">
      {/* Header */}
      <div ref={formTopRef} className="mb-5 flex items-center gap-4 px-2">
        <Button
          variant="ghost"
          size="lg"
          className="p-2 rounded-full hover:bg-gray-100 transition-colors"
          onClick={() => navigate(-1)}
        >
          <ArrowLeftCircle className="h-9 w-9 text-gray-600" style={{ width: "30px", height: "30px" }} />
        </Button>
        <div className="flex-grow">
          <h1 className="text-xl lg:text-2xl font-bold text-gray-900">Performance</h1>
          <p className="text-m text-gray-600 font-medium">
            Track top performers across different categories and time periods.
          </p>
        </div>
        <div className="flex gap-4 w-96">
          {/* Field Dropdown */}
          <div className="relative w-1/2">
            <select
              value={selectedField}
              onChange={(e) => setSelectedField(e.target.value)}
              className="w-full h-10 rounded-lg border border-gray-200 focus:border-orange-500 focus:ring-2 focus:ring-orange-100 bg-white px-4 pr-10 text-gray-700 appearance-none transition-colors duration-150 shadow-sm hover:border-orange-300"
              disabled={isLoading}
            >
              {fieldOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
              ▼
            </span>
          </div>
          {/* Period Dropdown */}
          <div className="relative w-1/2">
            <select
              value={selectedPeriod}
              onChange={(e) => setSelectedPeriod(e.target.value)}
              className="w-full h-10 rounded-lg border border-gray-200 focus:border-orange-500 focus:ring-2 focus:ring-orange-100 bg-white px-4 pr-10 text-gray-700 appearance-none transition-colors duration-150 shadow-sm hover:border-orange-300"
              disabled={isLoading}
            >
              {periodOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
              ▼
            </span>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <section
        className={`bg-gray-50 rounded-3xl p-6 lg:p-8 shadow-md w-full ${
          leaderboardToShow.length === 1 ? "!max-w-none !mx-0" : ""
        }`}
      >
        <div className="flex items-center justify-between mb-8">
          <div>
            <h2 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-2">
              {selectedPeriod.charAt(0).toUpperCase() + selectedPeriod.slice(1)} Top Performance
            </h2>
            <p className="text-gray-500">
              {selectedPeriod === "weekly"
                ? "This week's standout performers"
                : selectedPeriod === "monthly"
                ? "This month's standout performers"
                : selectedPeriod === "daily"
                ? "Today's standout performers"
                : "This quarter's standout performers"}
            </p>
          </div>
          <div className="flex items-center gap-2 px-4 py-2 bg-amber-50 rounded-xl border border-amber-200">
            <div className="w-2 h-2 bg-amber-500 rounded-full animate-pulse" />
            <span className="text-sm font-medium text-amber-600">Live</span>
          </div>
        </div>
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-orange-500" />
            <span className="ml-3 text-gray-600">Loading leaderboard data...</span>
          </div>
        ) : leaderboardToShow.length === 0 ? (
          <div className="flex items-center justify-center py-12">
            <p className="text-gray-500">No data available for the selected period and field.</p>
          </div>
        ) : (
          <div
            className={
              leaderboardToShow.length === 1
                ? ""
                : "grid grid-cols-1 md:grid-cols-2 gap-6"
            }
          >
            {leaderboardToShow.map((category, index) => (
              <CategoryLeaderboard
                key={`${selectedPeriod}-${index}`}
                title={category.title}
                performers={category.performers}
              />
            ))}
          </div>
        )}
      </section>
    </div>
  );
};